
import React from 'react';
import { Card, CardHeader, CardContent, CardFooter, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreOption } from '@/hooks/useTelemarketingScan';

interface ScanQuestionCardProps {
  theme: string | null;
  question: string;
  questionIndex: number;
  totalQuestions: number;
  currentAnswer: number | undefined;
  scoreOptions: ScoreOption[];
  onAnswerSelect: (value: number) => void;
  onNextQuestion: () => void;
  isLastQuestion: boolean;
  animateCard: boolean;
}

const ScanQuestionCard: React.FC<ScanQuestionCardProps> = ({
  theme,
  question,
  questionIndex,
  totalQuestions,
  currentAnswer,
  scoreOptions,
  onAnswerSelect,
  onNextQuestion,
  isLastQuestion,
  animateCard
}) => {
  return (
    <>
      <Card className={cn(
        "border-none shadow-lg transform transition-all duration-300",
        animateCard ? "translate-y-4 opacity-0" : "translate-y-0 opacity-100"
      )}>
        <CardHeader className="bg-gradient-to-r from-brand-lightBlue to-white border-b pb-6">
          <CardTitle className="text-2xl text-gray-900">
            {question}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="pt-6 pb-4">
          <div className="space-y-3">
            {scoreOptions.map((option) => (
              <div 
                key={option.label}
                className={cn(
                  "flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all",
                  currentAnswer === option.value 
                    ? "bg-brand-lightBlue border-2 border-brand-blue" 
                    : "border-2 border-transparent hover:bg-gray-50"
                )}
                onClick={() => onAnswerSelect(option.value)}
              >
                <div>
                  <h4 className="font-medium text-lg">{option.label}</h4>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
                <div className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center",
                  currentAnswer === option.value 
                    ? "border-brand-blue" 
                    : "border-gray-300"
                )}>
                  {currentAnswer === option.value && (
                    <div className="w-3 h-3 rounded-full bg-brand-blue"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
        
        <CardFooter className="pt-2 pb-6">
          <Button
            onClick={onNextQuestion}
            disabled={currentAnswer === undefined}
            className="ml-auto flex items-center group"
          >
            {isLastQuestion
              ? "Resultaten bekijken"
              : "Volgende vraag"}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </CardFooter>
      </Card>
      
      {/* Question count */}
      <div className="text-center mt-6 text-sm text-gray-500">
        Vraag {questionIndex + 1} van {totalQuestions}
        {theme && ` (${theme})`}
      </div>
    </>
  );
};

export default ScanQuestionCard;
